import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, CreditCard, BellRing, FileDown, Download, AlertTriangle, LockOpen } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Panel } from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";
import { TrackingTimeline } from "@/components/TrackingTimeline";
import { AuditTrail } from "@/components/AuditTrail";
import { LetterViewer } from "@/components/LetterViewer";
import { LetterSummary } from "@/components/LetterSummary";
import { api, formatError, fmtDate } from "@/lib/api";
import { FixContact } from "@/components/FixContact";
import { useAuth } from "@/context/AuthContext";

const Info = ({ k, v, mono }) => (
  <div className="py-2 border-b border-slate-100 dark:border-slate-800 last:border-0">
    <p className="text-[11px] uppercase tracking-wider text-slate-500">{k}</p>
    <p className={`text-sm mt-0.5 break-all ${mono ? "font-mono text-xs" : ""}`}>{v || "—"}</p>
  </div>
);

const PayButton = ({ id }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/mail/${id}/checkout`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      setErr(formatError(e));
      setBusy(false);
    }
  };
  return (
    <div>
      <button data-testid="complete-payment-btn" onClick={pay} disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-500 transition-colors">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <CreditCard size={15} />} Complete payment (£0.60)
      </button>
      {err && <p className="text-sm text-red-600 mt-2">{err}</p>}
    </div>
  );
};

const UnlockButton = ({ mail, onDone }) => {
  const [busy, setBusy] = useState(false);
  const unlock = async () => {
    setBusy(true);
    try { await api.post(`/mail/${mail.id}/unlock-claim`); toast.success("Unlocked. Your recipient can try again."); onDone(); } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };
  return (
    <button data-testid="unlock-claim-btn" onClick={unlock} disabled={busy} title="Your recipient entered details that didn't match 5 times" className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-amber-500 text-white text-sm font-semibold hover:bg-amber-400 transition-colors disabled:opacity-60">
      {busy ? <Loader2 size={15} className="animate-spin" /> : <LockOpen size={15} />} Unlock claim</button>
  );
};

const ResendButton = ({ mail, onDone }) => {
  const [busy, setBusy] = useState(false);
  const left = 3 - (mail.resend_count || 0);
  const resend = async () => {
    setBusy(true);
    try {
      await api.post(`/mail/${mail.id}/resend-notification`);
      toast.success("Notification resent");
      onDone();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button data-testid="resend-notification-btn" onClick={resend} disabled={busy || left <= 0}
      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-slate-300 dark:border-slate-700 text-sm font-semibold hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50">
      {busy ? <Loader2 size={15} className="animate-spin" /> : <BellRing size={15} />}
      Resend notification <span data-testid="resend-remaining" className="text-xs text-slate-500">({left} left)</span>
    </button>
  );
};

const DownloadButton = ({ path, filename, label, icon: Icon, testId, onDone, outline }) => {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      Object.assign(document.createElement("a"), { href: url, download: filename }).click();
      URL.revokeObjectURL(url);
      onDone();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };
  const style = outline
    ? "border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800"
    : "bg-slate-900 text-white dark:bg-white dark:text-slate-900 hover:bg-emerald-600 dark:hover:bg-emerald-500";
  return (
    <button data-testid={testId} onClick={download} disabled={busy}
      className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors disabled:opacity-60 ${style}`}>
      {busy ? <Loader2 size={15} className="animate-spin" /> : <Icon size={15} />} {label}
    </button>
  );
};

const DELIVERY_LABEL = { sent: "Sent", delivered: "Delivered", not_delivered: "Not delivered" };

const DeliveryBadges = ({ delivery, smsReason }) => {
  const items = [["claim_code", "Code not delivered"], ["notification", "Notification not delivered"], ["sms", "Text not delivered"]].filter(([k]) => delivery?.[k] === "not_delivered");
  return items.map(([k, label]) => (
    <span key={k} data-testid={`delivery-badge-${k}`} title={k === "sms" && smsReason ? smsReason : "We'll keep trying when the recipient asks again. You can also resend the notification."}
      className="inline-flex items-center gap-1.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 px-3 py-1 text-xs font-semibold">
      <AlertTriangle size={12} /> {label}</span>
  ));
};

export default function MailDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [error, setError] = useState("");
  const load = () => api.get(`/mail/${id}`).then((r) => setD(r.data)).catch((e) => setError(formatError(e)));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <p data-testid="mail-detail-error" className="text-red-600">{error}</p>;
  if (!d) return <Loader2 className="animate-spin text-emerald-600" />;
  const m = d.mail;
  const a = m.recipient_address || {};
  const canView = d.role === "sender" || (d.role === "recipient" && m.status === "claimed");

  return (
    <div data-testid="mail-detail-page">
      <PageHeader eyebrow={`${m.reference} · ${d.role}`} title={m.subject}>
        <div className="flex flex-wrap items-center gap-3">
          <StatusBadge status={m.status} testId="mail-detail-status" />
          {d.role === "sender" && m.status === "pending_claim" && <DeliveryBadges delivery={d.delivery} smsReason={d.sms_reason} />}
          {d.role === "sender" && d.can_act && m.status === "awaiting_payment" && !m.batch_id && <PayButton id={m.id} />}
          {d.role === "sender" && d.can_act && m.status === "pending_claim" && Object.values(d.delivery || {}).includes("not_delivered") && <FixContact mail={m} onDone={load} />}
          {d.role === "sender" && d.can_act && d.claim_locked && d.unlocks_remaining > 0 && <UnlockButton mail={m} onDone={load} />}
          {d.role === "sender" && d.can_act && m.status === "pending_claim" && <ResendButton mail={m} onDone={load} />}
          {canView && <DownloadButton path={`/mail/${m.id}/document?download=true`} filename={`PostalCloud-Letter-${m.reference}.pdf`} label="Download letter" icon={Download} testId="download-letter-btn" onDone={load} outline />}
          {m.status === "claimed" && <DownloadButton path={`/mail/${m.id}/certificate`} filename={`PostalCloud-Certificate-${m.reference}.pdf`} label="Delivery certificate" icon={FileDown} testId="download-certificate-btn" onDone={load} />}
        </div>
      </PageHeader>
      {d.role === "sender" && d.claim_locked && (
        <p data-testid="claim-locked-note" className="mb-6 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-4 text-sm text-amber-800 dark:text-amber-300 flex gap-2">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          {d.unlocks_remaining > 0
            ? `Your recipient entered a name or address that didn't match 5 times, so this claim is locked. Check that the recipient's name and address on this letter are correct, then unlock it (${d.unlocks_remaining} unlock${d.unlocks_remaining === 1 ? "" : "s"} left).`
            : "This claim is locked again and can't be unlocked any more. Please send a new letter with the recipient's correct name and address."}
        </p>
      )}
      <div className="grid xl:grid-cols-3 gap-6">
        <div className="space-y-6">
          <Panel title="Tracking" testId="tracking-panel"><TrackingTimeline mail={m} /></Panel>
          <Panel title="Details">
            <Info k="From" v={m.sender_email ? `${m.sender_name} (${m.sender_email})` : m.sender_name} />
            <Info k="To" v={m.recipient_name} />
            {d.role !== "recipient" && <Info k="Recipient email" v={m.recipient_email} />}
            {d.role !== "recipient" && <Info k="Postal address" v={[a.line1, a.line2, a.city, a.postcode].filter(Boolean).join(", ")} />}
            <Info k="Claim window ends" v={fmtDate(m.expires_at)} />
            <Info k="File SHA-256" v={m.file_sha256} mono />
            {d.id_check && (
              <Info k="ID check" v={d.id_check.status === "verified" ? `Verified ${fmtDate(d.id_check.decided_at)}` : d.id_check.status === "name_mismatch" ? "Name on ID didn't match" : d.id_check.status === "requires_input" ? "Not completed yet" : "Required, not started"} />
            )}
            {d.role === "sender" && m.status !== "awaiting_payment" && d.delivery && (
              <div data-testid="delivery-status">
                <Info k="Letter notification" v={DELIVERY_LABEL[d.delivery.notification] || "No result yet"} />
                <Info k="Claim code" v={DELIVERY_LABEL[d.delivery.claim_code] || "Not requested yet"} />
                {d.delivery.sms && <Info k="Text message" v={<span data-testid="delivery-sms-status">{DELIVERY_LABEL[d.delivery.sms]}{d.sms_reason && d.delivery.sms === "not_delivered" ? ` — ${d.sms_reason}` : ""}</span>} />}
                {m.recipient_phone && <Info k="Mobile number" v={d.phone_check === "valid_mobile" ? "Checked before sending" : d.phone_check === "unverified" ? "Couldn't be checked when sent" : "Not checked"} />}
              </div>
            )}
            {d.role === "sender" && m.status !== "awaiting_payment" && (
              <div data-testid="reply-status">
                <Info k="Replies" v={m.reply_to ? `Enabled: the recipient's email said they can reply, and replies go to ${m.reply_to}` : "Not enabled: no reply-to inbox was set when this letter was sent"} />
              </div>
            )}
          </Panel>
        </div>
        <div className="xl:col-span-2 space-y-6">
          {m.status === "claimed" && d.role !== "admin" && (
            <Panel title="AI summary" testId="summary-panel"><LetterSummary mailId={m.id} /></Panel>
          )}
          {canView && (
            <Panel title="Letter">
              <LetterViewer mailId={m.id} watermark={`${user?.name} · ${m.reference}`} />
            </Panel>
          )}
          <Panel title="Tamper-evident audit trail" testId="mail-audit-panel">
            <p className="text-xs text-slate-500 mb-4">Each event is SHA-256 hashed and linked to the previous event in the global chain.</p>
            <AuditTrail events={d.events} />
          </Panel>
        </div>
      </div>
    </div>
  );
}
